/**
 * The export control every report offers — CLAUDE.md §5.9.
 *
 * `POST /api/reports/{id}/export` re-runs the governed report request and streams the result. The
 * browser sends report intent, never its current rows.
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowDown } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@tanstack/react-query';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import type { ExportColumn } from '@/components/report/groupedColumns';
import { useI18n } from '@/lib/i18n';
import {
  ApiError,
  exportReport,
  type ExportFormat,
  type ExportedFile,
  type ReportQueryRequest,
} from '@/lib/api';

/** Hand the finished file to the browser under the name the server gave it. */
function saveFile({ blob, filename }: ExportedFile): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface ExportMenuProps {
  /** The report the reader is looking at right now — the active tab's id, not always the page's. */
  reportId: string;
  /** The exact request the visible table was queried with, filters and sort included. */
  request: ReportQueryRequest;
  /**
   * The grid on screen, so the file matches it (§5.9). Build it with `exportColumns(columns)` from
   * the very `ColumnDef[]` the `<DataTable>` got, so there is one list and not two that can drift.
   * Omit it and the server writes every column the view declares.
   */
  columns?: readonly ExportColumn[];
  /** True while the filters are incomplete — there is no result to export yet. */
  disabled?: boolean;
}

export function ExportMenu({
  reportId,
  request,
  columns,
  disabled = false,
}: ExportMenuProps) {
  const { locale, text } = useI18n();
  const download = useMutation({
    mutationFn: (format: ExportFormat) =>
      exportReport(
        reportId,
        format,
        {
          ...request,
          ...(columns === undefined || columns.length === 0 ? {} : { columns: [...columns] }),
        },
        locale,
      ),
    onSuccess: saveFile,
  });

  const failure =
    download.error === null
      ? null
      : download.error instanceof ApiError
        ? (download.error.problem.detail ?? download.error.problem.title)
        : download.error instanceof Error
          ? download.error.message
          : text('The export failed. Try again, or narrow the filters.');

  return (
    <div className="flex flex-col items-end gap-1">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5"
            disabled={disabled || download.isPending}
          >
            <FontAwesomeIcon icon={faFileArrowDown} className="size-3.5" aria-hidden />
            {download.isPending ? text('Exporting…') : text('Export')}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onSelect={() => {
              download.mutate('xlsx');
            }}
          >
            {text('Export to Excel')}
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={() => {
              download.mutate('csv');
            }}
          >
            {text('Export to CSV')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {failure === null ? null : (
        <p role="alert" className="text-xs text-destructive">
          {failure}
        </p>
      )}
    </div>
  );
}
